/**
 * Detects whether the active worksheet holds a list of company labels.
 *
 * Routing used to require the user to say "these companies" or name a column
 * before an aligned per-row workload could start. The workbook snapshot the
 * model already receives carries the active sheet's cells, so that context is
 * available without asking the user to restate it — which is what lets a bare
 * "Pull CEO names" resolve.
 *
 * Deliberately permissive: one populated text row is enough. The semantic
 * classifier decides whether the request wants per-row research; this only
 * confirms there is something to align against.
 */

import type { CellRange, WorkbookContent, WorksheetContent } from "./types";

export interface WorksheetCompanyList {
  present: boolean;
  sheetName?: string;
  /** Column letter of the detected label column, e.g. "A". */
  columnLetter?: string;
  /** 1-based row holding the header, when one was recognized. */
  headerRow?: number;
  /** 1-based first data row. */
  startRow?: number;
  /** 1-based last populated data row. */
  endRow?: number;
  rowCount?: number;
  /** True when the column header named companies explicitly. */
  hasCompanyHeader?: boolean;
}

const ABSENT: WorksheetCompanyList = { present: false };

// Match generic column labels as complete phrases. Searching for these words
// anywhere in row 0 mistakes issuer names such as "Company Inc." and
// "BlackRock Holdings" for headers and drops the only company on a short list.
const COMPANY_HEADER =
  /^(?:(?:ultimate\s+parent\s+)?compan(?:y|ies)(?:\s+(?:names?|list))?|issuers?(?:\s+names?)?|entit(?:y|ies)(?:\s+names?)?|tickers?(?:\s+symbols?)?|securit(?:y|ies)(?:\s+(?:names?|symbols?))?|symbols?|names?|counterpart(?:y|ies)(?:\s+names?)?|(?:portfolio\s+)?holdings?|positions?)$/i;

/**
 * Header words that mark a column as something other than a company label,
 * checked first so a sheet whose first column is "Date" or "Region" does not
 * masquerade as a company list.
 */
const NON_COMPANY_HEADER =
  /\b(?:dates?|months?|quarters?|years?|periods?|regions?|countr(?:y|ies)|sectors?|industr(?:y|ies)|currenc(?:y|ies)|notes?|comments?|status|categor(?:y|ies)|types?)\b/i;

const MAX_LABEL_LENGTH = 120;
const MAX_COLUMNS_SCANNED = 12;

function columnIndexToLetter(index: number): string {
  let remaining = index;
  let letter = "";
  do {
    letter = String.fromCharCode(65 + (remaining % 26)) + letter;
    remaining = Math.floor(remaining / 26) - 1;
  } while (remaining >= 0);
  return letter;
}

function isLabelLike(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const text = value.trim();
  if (!text || text.length > MAX_LABEL_LENGTH) return false;
  // A cell that parses cleanly as a number is data, not a label, even when the
  // host hands it back as a string.
  if (/^-?[\d,.\s%$€£¥]+$/.test(text)) return false;
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return false;
  return /[A-Za-z]/.test(text);
}

function findActiveSheet(
  snapshot: WorkbookContent,
): WorksheetContent | undefined {
  const byName = snapshot.activeSheetName
    ? snapshot.sheets?.find((sheet) => sheet.name === snapshot.activeSheetName)
    : undefined;
  return byName ?? snapshot.sheets?.[0];
}

interface ColumnScan {
  columnOffset: number;
  headerRowOffset: number | null;
  firstDataRowOffset: number;
  lastDataRowOffset: number;
  labelCount: number;
  hasCompanyHeader: boolean;
}

function scanColumn(
  usedRange: CellRange,
  columnOffset: number,
): ColumnScan | null {
  const rows = usedRange.values ?? [];
  if (rows.length === 0) return null;

  const headerValue = rows[0]?.[columnOffset];
  const headerText = typeof headerValue === "string" ? headerValue.trim() : "";
  const hasCompanyHeader =
    headerText.length > 0 &&
    COMPANY_HEADER.test(headerText) &&
    !NON_COMPANY_HEADER.test(headerText);
  if (headerText.length > 0 && NON_COMPANY_HEADER.test(headerText)) {
    return null;
  }

  // Treat row 0 as a header only when it reads like one. A single-row list
  // with no header still has to count.
  const headerRowOffset = hasCompanyHeader ? 0 : null;
  const firstScanned = headerRowOffset === null ? 0 : 1;

  let labelCount = 0;
  let nonLabelCount = 0;
  let firstDataRowOffset = -1;
  let lastDataRowOffset = -1;

  for (let row = firstScanned; row < rows.length; row++) {
    const cell = rows[row]?.[columnOffset];
    if (cell === null || cell === undefined || cell === "") continue;
    if (isLabelLike(cell)) {
      labelCount += 1;
      if (firstDataRowOffset < 0) firstDataRowOffset = row;
      lastDataRowOffset = row;
    } else {
      nonLabelCount += 1;
    }
  }

  if (labelCount === 0) return null;
  // A column of mostly numbers that happens to carry a stray word is a data
  // column, not a label column.
  if (labelCount < nonLabelCount) return null;

  return {
    columnOffset,
    headerRowOffset,
    firstDataRowOffset,
    lastDataRowOffset,
    labelCount,
    hasCompanyHeader,
  };
}

/**
 * Inspects the active sheet of a workbook snapshot for a company label column.
 * A column whose header names companies wins outright; otherwise the leftmost
 * predominantly textual column is used.
 */
export function detectWorksheetCompanyList(
  snapshot: WorkbookContent | undefined | null,
): WorksheetCompanyList {
  if (!snapshot || !Array.isArray(snapshot.sheets)) return ABSENT;

  const sheet = findActiveSheet(snapshot);
  const usedRange = sheet?.usedRange;
  if (!sheet || !usedRange || !Array.isArray(usedRange.values)) return ABSENT;

  const columnCount = Math.min(
    usedRange.columnCount || usedRange.values[0]?.length || 0,
    MAX_COLUMNS_SCANNED,
  );
  if (columnCount === 0) return ABSENT;

  let fallback: ColumnScan | null = null;
  let chosen: ColumnScan | null = null;

  for (let column = 0; column < columnCount; column++) {
    const scan = scanColumn(usedRange, column);
    if (!scan) continue;
    if (scan.hasCompanyHeader) {
      chosen = scan;
      break;
    }
    if (!fallback) fallback = scan;
  }

  const result = chosen ?? fallback;
  if (!result) return ABSENT;

  const baseRow = usedRange.startRow ?? 0;
  const baseColumn = usedRange.startColumn ?? 0;

  return {
    present: true,
    sheetName: sheet.name,
    columnLetter: columnIndexToLetter(baseColumn + result.columnOffset),
    headerRow:
      result.headerRowOffset === null
        ? undefined
        : baseRow + result.headerRowOffset + 1,
    startRow: baseRow + result.firstDataRowOffset + 1,
    endRow: baseRow + result.lastDataRowOffset + 1,
    rowCount: result.labelCount,
    hasCompanyHeader: result.hasCompanyHeader,
  };
}

/**
 * Convenience wrapper for callers that only hold the serialized snapshot the
 * executor receives as `workbookContext`.
 */
export function detectWorksheetCompanyListFromContext(
  workbookContext: string | undefined,
): WorksheetCompanyList {
  if (!workbookContext) return ABSENT;
  try {
    return detectWorksheetCompanyList(
      JSON.parse(workbookContext) as WorkbookContent,
    );
  } catch {
    return ABSENT;
  }
}
